import React, { useState } from 'react';
import { SimulationResult, QuantumConcept, SimulationStatus } from '../../types';
import DetectorCanvas from '../canvas/DetectorCanvas';
import ProbabilityBar from '../canvas/ProbabilityBar';

interface Props {
  result: SimulationResult | null;
  status: SimulationStatus;
  activePresetHint?: string;
  activePresetSteps?: string[];
}

const CONCEPT_COLORS: Record<QuantumConcept, { bg: string; fg: string }> = {
  superposition: { bg: '#ede9fe', fg: '#7c3aed' },
  measurement: { bg: '#fee2e2', fg: '#dc2626' },
  interference: { bg: '#dbeafe', fg: '#1d4ed8' },
  randomness: { bg: '#fef3c7', fg: '#b45309' },
  entanglement: { bg: '#d1fae5', fg: '#065f46' },
  'wave-particle-duality': { bg: '#fce7f3', fg: '#be185d' },
  decoherence: { bg: '#f1f5f9', fg: '#475569' },
};

const CONCEPT_ICONS: Record<QuantumConcept, string> = {
  superposition: '⚛️',
  measurement: '👁',
  interference: '🌊',
  randomness: '🎲',
  entanglement: '🔗',
  'wave-particle-duality': '〰️',
  decoherence: '💨',
};

const CONCEPT_DESCRIPTIONS: Record<QuantumConcept, string> = {
  superposition: 'A quantum particle can be in multiple states at once – until measured.',
  measurement: 'Observing a quantum system forces it to choose one definite state.',
  interference: 'Quantum paths can add together (constructive) or cancel out (destructive).',
  randomness: 'Quantum outcomes are truly random – not predictable even in principle.',
  entanglement: 'Two particles can share a quantum state and instantly affect each other.',
  'wave-particle-duality': 'Quantum particles behave as both waves and particles.',
  decoherence: 'Interaction with the environment destroys quantum superposition.',
};

export default function ResultsPanel({
  result,
  status,
  activePresetHint,
  activePresetSteps,
}: Props) {
  const [tab, setTab] = useState<'screen' | 'graph' | 'concepts' | 'steps'>('screen');

  const isRunning = (status as string) === 'running';
  const showScreen =
    result?.experimentType === 'double-slit' ||
    result?.experimentType === 'measurement-collapse';

  return (
    <div
      style={{
        width: 400,
        background: 'white',
        borderLeft: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, sans-serif',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px 0',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Results & Explanation
        </div>

        {/* Status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <StatusBadge status={status} />
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 2 }}>
          {([
            { key: 'screen', label: '📺 Screen', show: true },
            { key: 'graph', label: '📊 Graph', show: true },
            { key: 'concepts', label: '🧠 Concepts', show: true },
            { key: 'steps', label: '📝 Steps', show: !!activePresetSteps },
          ] as const).filter(t => t.show).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '5px 10px',
                fontSize: 11,
                border: 'none',
                borderRadius: '6px 6px 0 0',
                cursor: 'pointer',
                background: tab === t.key ? 'white' : 'transparent',
                color: tab === t.key ? '#6366f1' : '#64748b',
                fontWeight: tab === t.key ? 700 : 400,
                borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
        {/* ── Detector Screen Tab ─────────────────────────────────────── */}
        {tab === 'screen' && (
          <div>
            {isRunning && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#6366f1' }}>
                <div style={{ fontSize: 28, animation: 'spin 1s linear infinite', display: 'inline-block' }}>⚛️</div>
                <div style={{ marginTop: 8, fontSize: 13 }}>Simulating particles…</div>
              </div>
            )}

            {(status === 'complete' || status === 'idle') && (
              <>
                {showScreen ? (
                  <DetectorCanvas
                    result={result}
                    running={isRunning}
                    width={368}
                    height={200}
                  />
                ) : (
                  <div
                    style={{
                      background: '#f1f5f9',
                      borderRadius: 8,
                      height: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      fontSize: 13,
                      textAlign: 'center',
                      padding: 20,
                    }}
                  >
                    {result
                      ? 'This experiment uses the Probability Graph. Switch to the Graph tab!'
                      : 'Drag components and press ▶ Run to see the experiment!'}
                  </div>
                )}

                {result && (
                  <ExplanationBox text={result.explanation} />
                )}
              </>
            )}
          </div>
        )}

        {/* ── Graph Tab ──────────────────────────────────────────────── */}
        {tab === 'graph' && (
          <div>
            <ProbabilityBar result={result} width={368} height={170} />
            {result && (
              <div style={{ marginTop: 12 }}>
                <MeasurementSummary result={result} />
              </div>
            )}
          </div>
        )}

        {/* ── Concepts Tab ───────────────────────────────────────────── */}
        {tab === 'concepts' && (
          <div>
            {result?.conceptsIllustrated?.length ? (
              <>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  This experiment demonstrates:
                </div>
                {result.conceptsIllustrated.map((concept) => (
                  <ConceptCard key={concept} concept={concept} />
                ))}
              </>
            ) : (
              <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                Run an experiment to see which quantum concepts are illustrated!
              </div>
            )}
          </div>
        )}

        {/* ── Steps Tab ──────────────────────────────────────────────── */}
        {tab === 'steps' && activePresetSteps && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>
              Try these steps:
            </div>
            {activePresetSteps.map((step, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  marginBottom: 10,
                  padding: '10px 12px',
                  background: '#f8fafc',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#6366f1',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{step}</div>
              </div>
            ))}
            {activePresetHint && (
              <div
                style={{
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginTop: 8,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                  💡 Hint
                </div>
                <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
                  {activePresetHint}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: SimulationStatus }) {
  const isRunning = (status as string) === 'running';
  const config = {
    idle: { color: '#94a3b8', bg: '#f1f5f9', label: 'Ready to run' },
    running: { color: '#6366f1', bg: '#ede9fe', label: 'Simulating…' },
    complete: { color: '#059669', bg: '#d1fae5', label: 'Complete' },
    error: { color: '#dc2626', bg: '#fee2e2', label: 'Error' },
  }[status];

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        borderRadius: 20,
        background: config.bg,
        fontSize: 11,
        color: config.color,
        fontWeight: 600,
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: config.color,
          animation: isRunning ? 'pulse 1s ease-in-out infinite' : 'none',
        }}
      />
      {config.label}
    </div>
  );
}

function ExplanationBox({ text }: { text: string }) {
  return (
    <div
      style={{
        marginTop: 12,
        background: 'linear-gradient(135deg, #ede9fe, #dbeafe)',
        border: '1px solid #c4b5fd',
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', marginBottom: 5 }}>
        🔬 What happened?
      </div>
      <div style={{ fontSize: 12, color: '#3b0764', lineHeight: 1.6 }}>{text}</div>
    </div>
  );
}

function MeasurementSummary({ result }: { result: SimulationResult }) {
  const total = result.measurementCounts.zero + result.measurementCounts.one;
  if (total === 0) return null;

  const isEntanglement = result.experimentType === 'entanglement';

  if (isEntanglement) {
    const mismatches = (result.jointCounts?.zeroOne ?? 0) + (result.jointCounts?.oneZero ?? 0);
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, background: '#ede9fe', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#6366f1' }}>
            {result.measurementCounts.zero}
          </div>
          <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>|00⟩ outcomes</div>
        </div>
        <div style={{ flex: 1, background: '#fef3c7', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#d97706' }}>
            {result.measurementCounts.one}
          </div>
          <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600 }}>|11⟩ outcomes</div>
        </div>
        <div style={{ flex: 1, background: '#fee2e2', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>{mismatches}</div>
          <div style={{ fontSize: 10, color: '#991b1b', fontWeight: 600 }}>mismatches</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ flex: 1, background: '#ede9fe', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#6366f1' }}>
          {result.measurementCounts.zero}
        </div>
        <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>|0⟩ results</div>
      </div>
      <div style={{ flex: 1, background: '#fef3c7', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#d97706' }}>
          {result.measurementCounts.one}
        </div>
        <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600 }}>|1⟩ results</div>
      </div>
      <div style={{ flex: 1, background: '#f0fdf4', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{total}</div>
        <div style={{ fontSize: 10, color: '#065f46', fontWeight: 600 }}>total shots</div>
      </div>
    </div>
  );
}

function ConceptCard({ concept }: { concept: QuantumConcept }) {
  const colors = CONCEPT_COLORS[concept];
  return (
    <div
      style={{
        background: colors.bg,
        borderRadius: 10,
        padding: '10px 14px',
        marginBottom: 8,
        border: `1px solid ${colors.fg}33`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>{CONCEPT_ICONS[concept]}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: colors.fg, textTransform: 'capitalize' }}>
          {concept.replace(/-/g, ' ')}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.5 }}>
        {CONCEPT_DESCRIPTIONS[concept]}
      </div>
    </div>
  );
}
